import { startOfTomorrow } from 'date-fns/startOfTomorrow';
import React from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { useViewType, ViewType } from '@eika/hooks';
import { ModalDialogType } from '@eika/modal';

import { useConfig } from '../../../defaultConfig';
import { paymentFromSelector, useAccountFreeWithdrawals, useAccounts } from '../../../fetchHooks/useAccount';
import { useFirstBankingDayDispatch } from '../../../fetchHooks/useBankingDays';
import type { EFakturaId, PaymentInterval } from '../../../generated-types';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import filterInvalidAccounts from '../../../utils/filterInvalidAccounts';
import { PaymentDesktopFields, PaymentMobileFields } from './fields';
import FieldProps from './fields/FieldProps';
import { PaymentActionType } from './form/actions';
import useFormContext from './form/useFormContext';
import { useAmountLimitWarning } from './form/validate';

interface Props {
    locked?: boolean;
    paymentInterval?: PaymentInterval;
    lockNote?: boolean;
    lockReceiver?: boolean;
    shouldFocusFirstElement?: boolean;
    kidLoading?: boolean;
    isFuturePayment?: boolean;
    eFakturaId?: EFakturaId;
    isInModal?: boolean;
    useDesktopPlaceholder?: boolean;
    isActive?: boolean;
    showInstant?: boolean;
    inFocusModal?: boolean;
}

const today = getZonedDate();
const tomorrow = getZonedDate(startOfTomorrow());

const PaymentFields: React.FC<Props> = ({
    locked = false,
    lockNote,
    lockReceiver,
    paymentInterval,
    shouldFocusFirstElement = false,
    kidLoading = false,
    isFuturePayment = false,
    eFakturaId = undefined,
    isInModal = true,
    useDesktopPlaceholder = true,
    isActive = true,
    showInstant = false,
    inFocusModal = false,
}) => {
    const { featureToggles } = useConfig();
    const { state, handleChange } = useFormContext();

    const { data: validAccounts, ...accountState } = useAccounts({ select: paymentFromSelector });
    const { data: allAccounts } = useAccounts();
    const invalidAccounts = filterInvalidAccounts({ allAccounts, validAccounts });

    useFirstBankingDayDispatch(tomorrow, handleChange, PaymentActionType.FIRST_BANKING_DATE_FROM_TODAY);

    const paymentLimitsWarning = useAmountLimitWarning(state);

    const { targetFromAccount } = usePaymentAccountContext();
    const { data: freeWithdrawals, showFreewithdrawal } = useAccountFreeWithdrawals(
        targetFromAccount,
        featureToggles.enableGettingAccountWithdrawalsInPayment,
    );

    const waitingForReceiver = locked && !state.toAccount;
    const animateModalAs: ModalDialogType | undefined = isInModal ? undefined : 'sub';

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const fieldProps: FieldProps = {
        locked,
        shouldFocusFirstElement,
        freeWithdrawals: { show: showFreewithdrawal, data: freeWithdrawals },
        eFaktura: { id: eFakturaId, loading: kidLoading },
        receiverState: { isLoading: waitingForReceiver, lock: lockReceiver },
        kid: { isLoading: kidLoading, lock: lockNote || state.hasInitialKid },
        date: { minDate: today, interval: paymentInterval },
        isFuturePayment,
        animateModalAs,
        useDesktopPlaceholder,
        isActive,
        hideInstant: !showInstant,
        inFocusModal,
        paymentLimitsWarning,
        validAccounts,
        invalidAccounts,
        isLoading: accountState.isLoading,
        error: accountState.error,
        refetch: accountState.refetch,
    };

    return isMobile ? <PaymentMobileFields {...fieldProps} /> : <PaymentDesktopFields {...fieldProps} />;
};

export default PaymentFields;
